import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { reefGeometry, type ReefAsset } from '../game/world/reef-assets'
import type { SceneArt } from '../data/cutscenes'
import { cloneDioramaTerrainMaterial, fittedDioramaRock, projectDioramaTerrainUVs } from './DioramaTerrain'

/** Static miniature kit. Every material is baked into one batch, so small construction
 * details cost triangles, not hundreds of submissions. Action props stay separate. */
export class DioramaEnvironment {
  readonly root = new THREE.Group()
  private batches = new Map<THREE.Material, THREE.BufferGeometry[]>()
  private textures: THREE.Texture[] = []
  private materials: Record<string, THREE.MeshStandardMaterial> = {}
  private transform = new THREE.Object3D()
  private readonly waterTime = { value: 0 }

  constructor(readonly scene: SceneArt) {
    this.root.name = `AuthoredEnvironment_${scene}`
    this.makeMaterials()
    if (scene === 'town' || scene === 'krabbyshack' || scene === 'reopening') {
      this.restaurant()
      if (scene !== 'krabbyshack') this.town()
    } else if (scene === 'ship') this.ship()
    else if (scene === 'throne' || scene === 'royalcave') this.royal()
    else this.wilderness()
    this.oceanDepth()
    this.reefTransition()
    this.foreground()
    this.bake()
  }

  update(elapsed: number): void { this.waterTime.value = elapsed }

  dispose(): void {
    this.root.traverse((object) => {
      if (object instanceof THREE.Mesh) object.geometry.dispose()
    })
    Object.values(this.materials).forEach((material) => material.dispose())
    this.textures.forEach((texture) => texture.dispose())
    this.root.removeFromParent()
    this.root.clear()
  }

  private makeMaterials(): void {
    // Deterministic, tileable grain: relief only, preserving the authored albedo.
    const size = 128
    const data = new Uint8Array(size * size * 4)
    const woodData = new Uint8Array(size * size * 4)
    for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
      const noise = ((x * 1973 + y * 9277 + x * y * 26699) >>> 0) % 97
      const grain = Math.sin(x * Math.PI / 8 + Math.sin(y * Math.PI / 64) * 1.5)
      for (let channel = 0; channel < 3; channel++) {
        data[(y * size + x) * 4 + channel] = 116 + noise
        woodData[(y * size + x) * 4 + channel] = 140 + grain * 42 + noise * 0.3
      }
      data[(y * size + x) * 4 + 3] = woodData[(y * size + x) * 4 + 3] = 255
    }
    const texture = (pixels: Uint8Array<ArrayBuffer>) => {
      const map = new THREE.DataTexture(pixels, size, size, THREE.RGBAFormat)
      map.wrapS = map.wrapT = THREE.RepeatWrapping
      map.magFilter = THREE.LinearFilter
      map.minFilter = THREE.LinearMipmapLinearFilter
      map.generateMipmaps = true
      map.repeat.set(3, 3)
      map.needsUpdate = true
      this.textures.push(map)
      return map
    }
    // Three.js's image loader needs a browser DOM. Keep the real caustic map
    // for the game while making set construction safe in DOM-less unit tests.
    const caustics = typeof document === 'undefined'
      ? new THREE.Texture()
      : new THREE.TextureLoader().load(`${import.meta.env.BASE_URL}textures/aaa/caustics.png`)
    caustics.wrapS = caustics.wrapT = THREE.RepeatWrapping
    this.textures.push(caustics)
    const relief = texture(data)
    const grain = texture(woodData)
    const roles: [string, number, number, number][] = [
      ['sand', 0xc9af7b, 0.93, 0], ['stone', 0x577a80, 0.9, 0],
      ['deepstone', 0x343e58, 0.83, 0], ['wood', 0x98603a, 0.8, 0],
      ['paleWood', 0xcead78, 0.8, 0], ['teal', 0x237c86, 0.6, 0.05],
      ['cream', 0xe4dcc0, 0.74, 0], ['brass', 0xb58b3e, 0.33, 0.68],
      ['iron', 0x324349, 0.46, 0.62], ['coral', 0xc67270, 0.8, 0],
      ['kelp', 0x356c51, 0.74, 0], ['purple', 0x66517f, 0.65, 0.08],
      ['glass', 0x639da7, 0.18, 0.2], ['light', 0xffd99a, 0.38, 0],
      ['red', 0xa54c3d, 0.62, 0],
    ]
    for (const [name, color, roughness, metalness] of roles) {
      this.materials[name] = new THREE.MeshStandardMaterial({ color, roughness, metalness,
        bumpMap: name === 'wood' || name === 'paleWood' ? grain : relief,
        bumpScale: name === 'sand' || name === 'stone' ? 0.065 : 0.025,
        emissive: name === 'light' ? 0xffb958 : 0x000000,
        emissiveIntensity: name === 'light' ? 0.6 : 0,
      })
      if (['sand', 'stone', 'deepstone', 'coral', 'purple'].includes(name)) {
        const authored = cloneDioramaTerrainMaterial(name === 'sand' ? 'sand' : 'limestone',
          name === 'sand' ? 0xe2dac4 : name === 'deepstone' ? 0x3f6574 :
            name === 'coral' ? 0xff907c : name === 'purple' ? 0xc18be5 : 0x628a92)
        if (authored) {
          this.materials[name].dispose()
          this.materials[name] = authored.material
          this.textures.push(...authored.textures)
        }
      }
      // The same moving sunlight crosses architecture, coral and rocks. World
      // coordinates keep colonies attached to the water volume during camera cuts.
      if (!['light', 'glass'].includes(name)) {
        this.materials[name].onBeforeCompile = (shader) => {
          shader.uniforms.oceanPlate = { value: caustics }
          shader.uniforms.oceanClock = this.waterTime
          shader.vertexShader = `varying vec3 oceanPosition; varying vec3 oceanNormal;\n${shader.vertexShader}`
            .replace('#include <begin_vertex>', `#include <begin_vertex>
              oceanPosition = (modelMatrix * vec4(position, 1.0)).xyz;
              oceanNormal = normalize(mat3(modelMatrix) * normal);`)
          shader.fragmentShader = `varying vec3 oceanPosition; varying vec3 oceanNormal;
            uniform sampler2D oceanPlate; uniform float oceanClock;\n${shader.fragmentShader}`
            .replace('#include <color_fragment>', `#include <color_fragment>
              vec2 waterUv = (oceanPosition.xz + oceanPosition.y * vec2(0.32, 0.18)) * 0.115;
              float waterA = texture2D(oceanPlate, waterUv + vec2(oceanClock * 0.009, oceanClock * 0.005)).r;
              float waterB = texture2D(oceanPlate, waterUv * 1.27 + vec2(-oceanClock * 0.006, oceanClock * 0.008)).r;
              float sunlight = smoothstep(0.28, 0.88, max(waterA, waterB));
              float facing = 0.18 + 0.82 * max(0.0, dot(normalize(oceanNormal), normalize(vec3(-0.32, 1.0, -0.18))));
              diffuseColor.rgb *= 0.9 + sunlight * facing * 0.75;`)
        }
        this.materials[name].customProgramCacheKey = () => 'ocean-environment-caustics-v1'
      }
      if (name === 'kelp') this.materials[name].side = THREE.DoubleSide
      this.materials[name].name = `Miniature_${name}`
    }
  }

  private part(geometry: THREE.BufferGeometry, role: string, x: number, y: number, z: number,
    sx = 1, sy = 1, sz = 1, rx = 0, ry = 0, rz = 0): void {
    this.transform.position.set(x, y, z)
    this.transform.rotation.set(rx, ry, rz)
    this.transform.scale.set(sx, sy, sz)
    this.transform.updateMatrix()
    const buffer = geometry.index ? geometry.toNonIndexed() : geometry.clone()
    geometry.dispose()
    buffer.applyMatrix4(this.transform.matrix)
    if (['sand', 'stone', 'deepstone', 'coral', 'purple'].includes(role)) {
      projectDioramaTerrainUVs(buffer, role === 'sand' ? 2 : role === 'coral' ? 0.7 : 1.6)
    }
    // All baked primitives share exactly this attribute layout.
    for (const key of Object.keys(buffer.attributes)) if (!['position', 'normal', 'uv'].includes(key)) buffer.deleteAttribute(key)
    const material = this.materials[role]
    const batch = this.batches.get(material) ?? []
    batch.push(buffer)
    this.batches.set(material, batch)
  }

  private box(role: string, x: number, y: number, z: number, w: number, h: number, d: number, ry = 0): void {
    this.part(new RoundedBoxGeometry(1, 1, 1, 1, 0.055), role, x, y, z, w, h, d, 0, ry)
  }

  private cylinder(role: string, x: number, y: number, z: number, r: number, h: number, rx = 0): void {
    this.part(new THREE.CylinderGeometry(r, r, h, 16), role, x, y, z, 1, 1, 1, rx)
  }

  private path(role: string, points: number[][], radius: number): void {
    const curve = new THREE.CatmullRomCurve3(points.map((p) => new THREE.Vector3(...p as [number, number, number])))
    this.part(new THREE.TubeGeometry(curve, 20, radius, 6, false), role, 0, 0, 0)
  }

  private ring(role: string, x: number, y: number, z: number, r: number, tube: number, rx = 0, arc = Math.PI * 2): void {
    this.part(new THREE.TorusGeometry(r, tube, 6, 24, arc), role, x, y, z, 1, 1, 1, rx)
  }

  private rock(x: number, y: number, z: number, sx: number, sy: number, sz: number, role = 'stone', seed = 0): void {
    const geometry = new THREE.IcosahedronGeometry(1, 1)
    const position = geometry.attributes.position
    for (let i = 0; i < position.count; i++) {
      const px = position.getX(i), py = position.getY(i), pz = position.getZ(i)
      const distort = 1 + Math.sin(px * 7 + py * 4 + pz * 3 + seed) * 0.13
      position.setXYZ(i, px * distort, py * distort, pz * distort)
    }
    geometry.computeVertexNormals()
    this.part(fittedDioramaRock(seed, geometry), role, x, y, z, sx, sy, sz, 0, seed * 0.5, seed * 0.07)
  }

  private lantern(x: number, y: number, z: number): void {
    this.cylinder('iron', x, y + 0.36, z, 0.025, 0.65)
    this.cylinder('light', x, y, z, 0.12, 0.26)
    this.part(new THREE.ConeGeometry(0.28, 0.18, 16), 'brass', x, y + 0.22, z)
    this.cylinder('brass', x, y - 0.19, z, 0.19, 0.07)
    for (const dx of [-0.15, 0.15]) this.box('iron', x + dx, y, z, 0.025, 0.32, 0.04)
  }

  private barrel(x: number, z: number, scale = 1): void {
    const profile = [[0.34,0],[0.4,0.1],[0.46,0.5],[0.43,0.9],[0.35,1]].map(([r,y]) => new THREE.Vector2(r * scale, y * scale))
    this.part(new THREE.LatheGeometry(profile, 16), 'wood', x, 0.02, z)
    for (const y of [0.14, 0.82]) this.ring('iron', x, y * scale, z, 0.4 * scale, 0.04 * scale, Math.PI / 2)
    this.cylinder('paleWood', x, scale, z, 0.35 * scale, 0.055)
  }

  private porthole(x: number, y: number, z: number, r = 0.48): void {
    this.cylinder('glass', x, y, z, r, 0.08, Math.PI / 2)
    this.ring('brass', x, y, z + 0.06, r, 0.075)
    for (let i = 0; i < 8; i++) {
      const angle = i * Math.PI / 4
      this.part(new THREE.SphereGeometry(0.034, 6, 4), 'iron', x + Math.cos(angle) * r, y + Math.sin(angle) * r, z + 0.13)
    }
  }

  private restaurant(): void {
    // Open-front theatrical cutaway with deep side returns and a barrel-vault roof.
    this.box('wood', 0, -0.12, -2.3, 9.4, 0.2, 5.1)
    for (let row = 0; row < 16; row++) {
      const z = -4.6 + row * 0.31
      this.box(row % 3 ? 'paleWood' : 'wood', 0, -0.01, z, 9.15, 0.1, 0.285)
    }
    for (let row = 0; row < 14; row++) {
      this.box(row % 4 === 0 ? 'wood' : 'teal', 0, 0.3 + row * 0.31, -4.45, 9.1, 0.29, 0.24)
      for (const x of [-4.45, 4.45]) this.box('wood', x, 0.3 + row * 0.31, -3.05, 0.22, 0.29, 2.9)
    }
    for (const x of [-4.55, -2.3, 2.3, 4.55]) this.box('iron', x, 2.1, -4.21, 0.14, 4.2, 0.17)
    // Curved roof shell, deliberately open toward the camera.
    const roof = new THREE.CylinderGeometry(4.7, 4.7, 3.2, 32, 1, true, -Math.PI / 2, Math.PI)
    this.part(roof, 'red', 0, 4.25, -3.1, 1, 1, 0.35, -Math.PI / 2)
    for (const z of [-4.65, -3.85, -3.05, -1.5]) {
      const points = Array.from({ length: 17 }, (_, i) => {
        const angle = i / 16 * Math.PI
        return [Math.cos(angle) * 4.7, 4.2 + Math.sin(angle) * 1.65, z]
      })
      this.path('brass', points, 0.055)
    }
    // Back-wall pass and kitchen, visible above the counter.
    this.box('iron', 0.9, 2.42, -4.2, 3.6, 1.65, 0.16)
    this.box('cream', 0.9, 2.36, -4.05, 3.3, 1.35, 0.13)
    for (let i = 0; i < 7; i++) this.box('iron', -0.65 + i * 0.52, 2.37, -3.96, 0.025, 1.25, 0.025)
    for (let i = 0; i < 3; i++) this.box('iron', 0.9, 1.99 + i * 0.38, -3.95, 3.2, 0.025, 0.025)
    this.box('iron', 0.9, 1.6, -3.7, 3.65, 0.22, 0.95)
    this.box('brass', 0.9, 3.28, -3.84, 3.8, 0.22, 0.85)
    this.box('iron', 0.9, 3.64, -4, 2.55, 0.48, 0.55)
    for (let i = 0; i < 9; i++) this.box('brass', -0.12 + i * 0.25, 3.63, -3.69, 0.12, 0.22, 0.035)
    this.box('wood', -3.12, 1.62, -4.14, 1.7, 2.92, 0.24)
    this.porthole(-3.12, 2.26, -3.94, 0.42)
    this.cylinder('brass', -2.47, 1.38, -3.95, 0.07, 0.1, Math.PI / 2)
    this.box('wood', 0, 0.78, -2.06, 6.5, 1.32, 0.9)
    this.box('iron', 0, 1.48, -2.02, 6.85, 0.16, 1.22)
    for (let i = 0; i < 19; i++) this.box(i % 3 ? 'wood' : 'paleWood', -3 + i / 3, 0.76, -1.59, 0.3, 1.18, 0.05)
    this.path('brass', [[-3,0.28,-1.25],[-3,0.22,-1.1],[3,0.22,-1.1],[3,0.28,-1.25]], 0.055)
    for (const x of [-2.2, 0, 2.2]) {
      this.lantern(x, 3.5, -2.65)
      this.cylinder('cream', x, 1.62, -2, 0.27, 0.08)
      this.cylinder('red', x + 0.43, 1.74, -2, 0.065, 0.36)
      this.part(new THREE.ConeGeometry(0.05,0.12,8), 'cream', x + 0.43, 1.98, -2)
    }
    // Coin-shaped restaurant emblem: bun, patty and relish are actual forms.
    this.cylinder('brass', 0, 5.18, -1.34, 0.61, 0.12, Math.PI / 2)
    this.part(new THREE.SphereGeometry(0.36,16,8,0,Math.PI*2,0,Math.PI/2), 'paleWood', 0,5.1,-1.16,1,0.65,0.8)
    this.box('wood',0,5.05,-1.16,0.65,0.1,0.45)
    this.box('kelp',0,5.13,-1.16,0.72,0.045,0.5)
    // Dining niches sit outside the acting corridor.
    for (const x of [-5.6,5.6]) {
      this.cylinder('iron', x,0.5,-1.15,0.09,1)
      this.cylinder('paleWood',x,1.03,-1.15,0.74,0.12)
      for (const dx of [-0.8,0.8]) {
        this.cylinder('iron',x+dx,0.3,-0.65,0.06,0.6)
        this.cylinder('red',x+dx,0.63,-0.65,0.26,0.15)
      }
      this.cylinder('cream',x,1.12,-1.15,0.22,0.05)
      this.barrel(x,-3,0.8)
    }
    if (this.scene === 'reopening') this.bunting()
  }

  private town(): void {
    for (const side of [-1,1]) {
      const x = side * 6.2
      this.box(side < 0 ? 'coral' : 'purple', x,1.85,-5.9,2.75,3.6,2.6,side * -0.13)
      for (let row=0; row<9; row++) {
        this.box(side < 0 ? 'coral' : 'purple',x,0.32+row*0.38,-4.53,2.72,0.34,0.09)
        for (const edge of [-1,1]) this.box('cream',x+edge*1.25,0.32+row*0.38,-4.47,0.16,0.3,0.13)
      }
      this.box('stone',x,0.12,-5.9,2.92,0.24,2.8)
      this.part(new THREE.ConeGeometry(2,1.4,8), 'teal',x,4.25,-5.9,1,1,0.85)
      for (let tier=0; tier<4; tier++) {
        const radius=1.9-tier*0.43
        this.part(new THREE.ConeGeometry(radius,0.38,8,1,true),'teal',x,3.76+tier*0.32,-5.9,1,1,0.85)
      }
      this.porthole(x,2.25,-4.49,0.6)
      this.box('wood',x,0.78,-4.45,0.8,1.55,0.15)
      for (let i=0;i<5;i++) this.box(i%2?'cream':'red',x-1.15+i*0.57,3.35,-4.2,0.55,0.14,1.2)
      this.lantern(x-side*1.55,2.9,-2)
      this.cylinder('iron',x-side*1.55,1.8,-2,0.055,3.6)
    }
    for (let i=0;i<18;i++) {
      const z = -6+i*0.53
      for (const side of [-1,1]) this.box('stone',side*(4.9+Math.sin(i*0.2)*0.5),0.06,z,0.38,0.16,0.47)
    }
  }

  /** Clone cached Blender meshes before fitting and baking them into the stage.
   * Height is measured from the attachment point, preserving all acting lanes. */
  private reefPart(asset: ReefAsset, role: string, x: number, y: number, z: number,
    width: number, height: number, depth: number, rotation = 0): boolean {
    const source = reefGeometry(asset)
    if (!source) return false
    const geometry = source.clone()
    geometry.computeBoundingBox()
    const bounds = geometry.boundingBox!
    const centre = bounds.getCenter(new THREE.Vector3())
    const size = bounds.getSize(new THREE.Vector3())
    geometry.translate(-centre.x, -bounds.min.y, -centre.z)
    geometry.scale(width / size.x, height / size.y, depth / size.z)
    projectDioramaTerrainUVs(geometry, 0.7)
    this.part(geometry, role, x, y, z, 1, 1, 1, 0, rotation)
    return true
  }

  private coral(x: number, z: number, size: number, role='coral'): void {
    // Reserve the dense antler mesh for hero-sized colonies. Smaller
    // foreground and transition colonies use the lighter encrusting asset so
    // the full underwater set stays within the browser triangle budget.
    if (size >= 1.05 && this.reefPart('coral-antlers', role, x, 0.025, z, size * 1.15, size * 1.8,
      size * 0.85, Math.sin(x * 2 + z) * 0.65)) {
      this.reefPart('encrusting-coral-patch', role, x, 0.015, z, size * 0.85,
        size * 0.17, size * 0.65, x)
      return
    }
    // Headless/bootstrap fallback retains the same bounds; live scenes preload the kit.
    const branches = [[0,0,0],[0.05,0.6,0],[0.12,1.2,0.03],[0.03,1.8,0]]
    this.path(role, branches.map(([a,b,c])=>[x+a*size,b*size,z+c*size]),0.1*size)
    for (const side of [-1,1]) for (let i=0;i<2;i++) {
      const y = (0.45+i*0.5)*size
      this.path(role,[[x,y,z],[x+side*0.38*size,y+0.2*size,z+0.12*size],[x+side*0.5*size,y+0.65*size,z+0.06*size]],0.07*size)
    }
  }

  private kelp(x: number, z: number, size: number): void {
    if (this.reefPart('kelp-ribbons', 'kelp', x, 0, z, 1.1 * size, 2.5 * size, 0.9 * size, x * 0.3)) return
    for (let i=0;i<3;i++) {
      const points = [[x,0,z],[x+Math.sin(i)*0.25,0.8*size,z],[x+Math.cos(i)*0.5,1.7*size,z+0.1],[x+Math.sin(i+1)*0.7,2.5*size,z+0.3]]
      this.path('kelp',points,0.065)
      for(let j=1;j<4;j++) {
        const y=j*0.55*size
        this.part(new THREE.SphereGeometry(1,10,6), 'kelp',x+Math.sin(i+j)*0.28,y,z+0.1,0.22,0.55*size,0.065,0,0,(i%2?1:-1)*0.5)
      }
    }
  }

  private wilderness(): void {
    const cave = this.scene === 'coralcave' || this.scene === 'deepsea'
    const rockRole = cave || this.scene === 'storm' ? 'deepstone' : 'stone'
    // Eroded terrain masses in back, side shelves in the near plane. Center stays clear.
    for(let i=0;i<9;i++) {
      const x=-7+i*1.75
      const h=1.1+Math.sin(i*1.8)*0.35+(cave?1.4:0)
      this.rock(x,h*0.6,-5.4-(i%2)*0.6,1.3,h,1.4,rockRole,i)
    }
    for(const side of [-1,1]) {
      // Three staggered eroded outcrops replace the broad, unnaturally flat shelf.
      this.rock(side*6.5,0.2,-2.1,1.25,0.85,1.45,rockRole,side+3)
      this.rock(side*6.1,0.12,-0.7,0.85,0.48,1.1,rockRole,side+6)
      this.rock(side*7.1,0.6,-2.6,0.85,1.15,0.85,rockRole,side+4)
      this.reefPart('encrusting-coral-patch', cave ? 'purple' : 'coral', side*6.1,0.5,-1.1,1.25,0.25,0.9,side*0.8)
      this.coral(side*5.7,-3.2,1.5,cave?'purple':'coral')
      this.coral(side*7.3,-0.4,0.7)
    }
    if (cave) {
      // A real tunnel arch with two recessed ribs, not a colored backdrop card.
      for(const z of [-4.6,-6]) for(let i=0;i<9;i++) {
        const a=i/8*Math.PI
        this.rock(Math.cos(a)*4.9,Math.sin(a)*4.5,z,1,0.85,0.95,rockRole,i)
      }
      for(let i=0;i<12;i++) {
        const x=(i%2?1:-1)*(4.5+(i%3)*0.7)
        const h=0.6+(i%4)*0.3
        this.part(new THREE.ConeGeometry(0.24,h,6),'purple',x,h/2,-4+(i%3),1,1,1,0,0,(i%2?1:-1)*0.2)
        this.part(new THREE.SphereGeometry(0.1,8,6),'light',x,h,-4+(i%3))
      }
    }
    if (this.scene === 'deepsea') {
      // Hydrothermal chimneys and a scalloped pearl grotto distinguish the
      // abyss chapter from the earlier electric coral tunnels.
      for (const side of [-1, 1]) for (let i = 0; i < 3; i++) {
        const x = side * (4.9 + i * 0.6), z = -3.3 + i * 0.45
        const profile = [[0.42,0],[0.36,0.5],[0.23,1.4],[0.29,1.65],[0.17,1.65],[0.16,1.4]]
          .map(([r,y]) => new THREE.Vector2(r, y * (1 + i * 0.22)))
        this.part(new THREE.LatheGeometry(profile,12),'deepstone',x,0,z)
        this.ring('glass',x,1.64*(1+i*0.22),z,0.22,0.065,Math.PI/2)
      }
      for (let i = 0; i < 11; i++) {
        const a = i / 10 * Math.PI
        this.path('cream',[[Math.cos(a)*1.6,0.15,-3.25],
          [Math.cos(a)*1.45,Math.sin(a)*1.45+0.2,-4],
          [Math.cos(a)*1.1,Math.sin(a)*1.7+0.25,-4.2]],0.085)
      }
      for (const x of [-5.3,5.3]) this.lantern(x,2.7,-4.2)
    }
    if (this.scene==='jellytrail'||this.scene==='pattyreef') {
      for(let i=0;i<7;i++) this.coral(-6+i*2,-4.6,0.8+(i%3)*0.3)
      for(let i=0;i<11;i++) this.rock(-3.5+i*0.65,0.02,-0.5-Math.sin(i*0.45)*1.3,0.4,0.09,0.32,'sand',i)
      if(this.scene==='pattyreef') for(let i=0;i<6;i++) {
        const x=-5+i*2
        this.rock(x,0.75,-3.7,0.9,0.35,0.9,'paleWood',i)
        this.rock(x,0.5,-3.7,0.95,0.16,0.95,'wood',i)
        this.rock(x,0.3,-3.7,1,0.12,1,'kelp',i)
      }
    }
    if(this.scene==='seaweed') {
      for(let i=0;i<10;i++) this.kelp(-7+i*1.5,-4.6-(i%2),1.2+(i%3)*0.2)
      for(const x of [-5.3,5.3]) this.kelp(x,-0.7,1)
      this.rock(-3.6,0.65,-1.8,0.65,0.9,0.75,'stone',1)
      this.rock(-2.6,0.65,-1.9,0.45,0.95,0.7,'stone',2)
    }
    if(this.scene==='bubblebridge') {
      for(const x of [-5,5]) {
        this.rock(x,0.35,-1.9,1.3,0.65,2.1,'sand',3)
        for(const z of [-3.5,-0.7]) { this.cylinder('stone',x,0.9,z,0.23,1.8); this.ring('brass',x,1.2,z,0.25,0.05,Math.PI/2) }
      }
      this.path('cream',[[-5,1.6,-3.5],[0,0.75,-3.5],[5,1.6,-3.5]],0.045)
    }
    if(this.scene==='storm') {
      for(let tier=0;tier<6;tier++) {
        const r=2.3-tier*0.22
        for(let i=0;i<9;i++) {
          const angle=i/9*Math.PI*2
          this.rock(Math.cos(angle)*r,0.4+tier*0.7,-4.2+Math.sin(angle)*r,0.7,0.5,0.65,'deepstone',i+tier)
        }
      }
      for(let i=0;i<17;i++) {
        const a=i*0.18
        this.box('stone',Math.sin(a)*2.8,0.15+i*0.2,-2.6-Math.cos(a)*1.4,0.85,0.18,0.55,-a)
      }
      this.path('iron',[[-1.2,4.1,-4.2],[-1.2,5.4,-4.2],[0,5.65,-4.2],[1.2,5.4,-4.2],[1.2,4.1,-4.2]],0.09)
    }
  }

  private ship(): void {
    // Raised cutaway stern, ribs, clinker planking and broken mast frame the deck.
    for(let row=0;row<7;row++) {
      const y=0.2+row*0.35
      this.path(row%2?'wood':'paleWood',[[-5.7,y,-1.8],[-4.7,y,-4.5],[0,y,-5.7],[4.7,y,-4.5],[5.7,y,-1.8]],0.19)
    }
    for(const x of [-4.6,-2.6,0,2.6,4.6]) this.path('iron',[[x,0,-4.8],[x,1.5,-4.8],[x,2.6,-4.55]],0.085)
    this.box('wood',0,2.2,-5,4,3.3,1.1)
    this.box('iron',0,3.9,-4.9,4.3,0.2,1.6)
    this.porthole(-1,2.9,-4.37,0.4)
    this.porthole(1,2.9,-4.37,0.4)
    this.box('paleWood',0,1.2,-4.37,1.05,2,0.18)
    this.cylinder('wood',-3.6,2.75,-3.6,0.16,5.5)
    this.path('cream',[[-5.4,0.3,-1.8],[-3.6,5.4,-3.6],[1.6,1.5,-4.6]],0.035)
    this.path('wood',[[-4.8,4.5,-3.6],[-3.6,4.55,-3.6],[-1.2,4.4,-3.6]],0.1)
    this.barrel(4,-1.3)
    this.barrel(-4.7,-1,0.8)
    // Broken overhead timber gives Zap a specific rescue anchor without a
    // ceiling or raised deck between the camera and the performers.
    this.box('wood', 2.7, 1.55, 0.45, 0.22, 3.1, 0.22)
    this.path('wood',[[2.7,3.02,0.45],[1.5,3.02,0.45],[0.9,2.92,0.48]],0.12)
    this.path('cream',[[1.05,2.93,0.48],[1.05,2.65,0.52],[1,2.4,0.58]],0.045)
    for (const x of [-6.6,6.6]) this.rock(x,-1.4,2,1.4,1.8,1,'deepstone',4)
    this.lantern(0,3.4,-4.05)
    this.ring('wood',2.8,2.5,-4.05,0.57,0.07)
    for(let i=0;i<8;i++) {
      const a=i*Math.PI/4
      this.path('wood',[[2.8,2.5,-4.05],[2.8+Math.cos(a)*0.75,2.5+Math.sin(a)*0.75,-4.05]],0.04)
    }
    this.cylinder('iron',2.8,1.3,-4.1,0.08,2.2)
  }

  private royal(): void {
    for(let i=0;i<11;i++) this.rock(-7+i*1.4,1.7,-5.6,1.2,2.2+(i%3)*0.5,1,'deepstone',i)
    for(const x of [-5,-3.7,3.7,5]) {
      this.cylinder('purple',x,2.1,-3.8,0.32,4.2)
      for(const y of [0.25,0.55,3.6,3.95]) this.cylinder('brass',x,y,-3.8,0.44,0.16)
      this.part(new THREE.OctahedronGeometry(0.47,0),'glass',x,4.45,-3.8,1,1.5,1)
      this.path('brass',[[x,0.5,-3.42],[x+0.15,1.5,-3.42],[x-0.15,2.6,-3.42],[x,3.5,-3.42]],0.035)
    }
    for(let i=0;i<3;i++) this.box('purple',0,0.08+i*0.12,-3.4-i*0.2,6.4-i*0.7,0.16,2.8-i*0.5)
    this.path('purple',[[-3.7,3.8,-4.1],[-2.7,5.1,-4.4],[0,5.8,-4.5],[2.7,5.1,-4.4],[3.7,3.8,-4.1]],0.42)
    this.path('brass',[[-3.7,3.9,-3.9],[-2.7,5.2,-4.2],[0,5.9,-4.3],[2.7,5.2,-4.2],[3.7,3.9,-3.9]],0.065)
    for(const x of [-5.8,5.8]) {
      this.rock(x,0.5,-0.7,1.3,0.7,1.7,'deepstone',2)
      for(let i=0;i<5;i++) this.part(new THREE.ConeGeometry(0.22,1.4+(i%3)*0.4,6),'purple',x+(i-2)*0.25,0.8,-0.8+(i%2)*0.3,1,1,1,0,0,(i-2)*0.15)
    }
    if (this.scene === 'royalcave') {
      // Empty audience stools make the King's loneliness visible before
      // the throne battle: he built a place for company, then stole obedience.
      for (const side of [-1, 1]) for (let i = 0; i < 3; i++) {
        const x = side * (3.8 + i * 0.65), z = -1.8 + i * 0.35
        this.cylinder('purple',x,0.38,z,0.3,0.7)
        this.cylinder('brass',x,0.77,z,0.35,0.09)
        this.cylinder('cream',x,0.84,z,0.27,0.09)
      }
    }
    // Hoarded coin piles are numerous shallow discs, not yellow rocks.
    for(let i=0;i<75;i++) {
      const side=i%2?1:-1
      this.cylinder('brass',side*(2.5+(i%7)*0.28),0.1+Math.floor(i/21)*0.06,-2.5+(i%5)*0.16,0.14,0.055)
    }
    this.lantern(-3,2.1,-3.8)
    this.lantern(3,2.1,-3.8)
  }

  private bunting(): void {
    this.path('iron',[[-7,4,-1],[-3,3.7,-1],[0,4.2,-1],[3,3.7,-1],[7,4,-1]],0.025)
    for(let i=0;i<20;i++) {
      const x=-6.7+i*0.7
      const y=3.9+Math.cos(x)*0.14
      this.part(new THREE.ConeGeometry(0.18,0.48,3),'coral',x,y-0.22,-1,1,1,0.18,0,0,Math.PI)
      this.part(new THREE.SphereGeometry(0.065,8,6),'light',x,y+0.1,-1)
    }
  }

  /** Extend the same physical world behind and beside the acting space. Reuse
   * authored reef meshes and PBR surfaces, with larger silhouettes at depth. */
  private oceanDepth(): void {
    const dark = ['deepsea', 'coralcave', 'royalcave', 'throne', 'storm'].includes(this.scene)
    const bed = this.scene === 'ship' ? -3.2 : 0
    // Asymmetric reef escarpments leave a long open water corridor behind the
    // cast. Distant formations diminish into blue haze instead of making a wall.
    for (let layer = 0; layer < 2; layer++) {
      const z = -17 - layer * 13
      for (const side of [-1, 1]) for (let i = 0; i < 4; i++) {
        const x = side * (12 + i * 4.6 + layer * 1.7)
        const height = 3.2 + (Math.sin(i * 2.7 + layer + side) + 1) * 2.2
        this.rock(x, bed + height * 0.22, z - i * 2.1,
          3.2 + i * 0.35, height, 3.7, dark ? 'deepstone' : 'stone', i + layer * 13)
        if ((i + layer) % 2 === 0) this.reefPart('kelp-ribbons', 'kelp', x + side * 1.5, bed, z + 2,
          2.1, height + 2.5, 1.7, i * 0.8)
        if (i === 0 && layer % 2 === 0) this.reefPart('coral-antlers', i % 2 ? 'purple' : 'coral', x - side, bed + 0.3,
          z + 3.8, 2.8, 2 + (i % 3), 2.2, i)
      }
    }
    for (const side of [-1, 1]) for (let i = 0; i < 4; i++) {
      const x = side * (9.5 + (i % 3) * 2.6), z = 9 - i * 3.5
      this.rock(x, bed + 0.45, z, 1.8, 1.1 + (i % 3) * 0.5, 2.1, 'stone', i + 40)
      if (i === 0 && side === 1) this.reefPart('coral-antlers', i % 2 ? 'purple' : 'coral', x, bed + 0.8, z,
        1.8, 2.1, 1.5, i * 0.7)
      if (i % 2 === 0) this.reefPart('kelp-ribbons', 'kelp', x + side, bed, z - 0.6,
        1.3, 4.5 + (i % 3), 1.2, i)
      this.reefPart('encrusting-coral-patch', 'coral', x - side * 0.8, bed + 0.8, z + 0.5,
        2, 0.3, 1.8, i)
    }
  }

  /** Low colonies connect the foreground reef to the broad sand basin without
   * enclosing the cast in a ring or intruding on their blocking corridor. */
  private reefTransition(): void {
    const bed = this.scene === 'ship' ? -3.2 : 0
    for (const side of [-1, 1]) for (let i = 0; i < 8; i++) {
      const z = -9.5 + i * 1.7
      const x = side * (5.4 + Math.sin(i * 1.73 + side) * 0.65 + Math.max(0, z) * 0.28)
      const size = 0.22 + (Math.sin(i * 3.1 + side) + 1) * 0.17
      this.rock(x, bed - 0.03, z, size * 1.6, size * 0.55, size * 1.25, 'stone', i + 87)
      this.reefPart('encrusting-coral-patch', i % 3 ? 'purple' : 'coral',
        x + side * 0.25, bed + 0.025, z + 0.25, size * 2.8, size * 0.65, size * 2.1, i * 1.3)
      if (i % 3 === 0) this.reefPart('coral-antlers', 'coral',
        x + side * 0.6, bed, z - 0.25, size * 1.4, size * 2.2, size, i)
    }
  }

  private foreground(): void {
    // Overlapping colonies have different scales and silhouettes. The central
    // path remains clear for every authored walk, prop exchange and reverse shot.
    for (const side of [-1, 1]) for (let i = 0; i < 6; i++) {
      const x = side * (6.9 + (i % 3) * 1.15)
      const z = 5.8 - i * 1.35
      const size = 0.45 + (i % 4) * 0.18
      this.rock(x, -0.12, z, size * 1.4, size * 0.65, size * 1.2, 'deepstone', i + 51)
      this.coral(x + side * 0.35, z - 0.2, size, i % 2 ? 'purple' : 'coral')
      this.reefPart('encrusting-coral-patch', i % 3 ? 'coral' : 'purple',
        x - side * 0.4, 0.12, z + 0.2, size * 1.7, size * 0.4, size * 1.4, i)
      if (i % 3 === 0) this.kelp(x + side * 0.7, z - 0.5, size * 1.25)
      for (let j = 0; j < 4; j++) {
        this.part(new THREE.SphereGeometry(0.16, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2),
          'cream', x + Math.sin(j * 2.4) * size, 0.025, z + Math.cos(j * 2.4) * size,
          1, 0.35, 0.7, 0, j * 0.8)
      }
    }
  }

  private bake(): void {
    let triangles=0
    for(const [material,geometries] of this.batches) {
      const geometry=mergeGeometries(geometries,false)
      geometries.forEach((part)=>part.dispose())
      if(!geometry) throw new Error('Unable to bake miniature environment')
      const mesh=new THREE.Mesh(geometry,material)
      mesh.name=`EnvironmentBatch_${material.name}`
      mesh.castShadow=mesh.receiveShadow=true
      this.root.add(mesh)
      triangles+=geometry.attributes.position.count/3
    }
    this.root.userData.diagnostics={drawCalls:this.root.children.length,triangles,textures:this.textures.length,materials:this.batches.size}
    this.batches.clear()
  }
}
